import {
  ArrowLeft,
  ArrowRight,
  Info,
  Loader2,
  Save,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { getSelfRetailerId } from "@/components/feature/auth/session";
import { KYC_FEE } from "@/pages/retailer/wallet/constants";
import { formatINR } from "./data";
import { REQUIRED_DOCS, PERSONAL_DOCS } from "./data";
import { emptyForm, type FormErrors, type KycForm } from "./types";
import Stepper from "./components/Stepper";
import BusinessDetailsStep from "./components/BusinessDetailsStep";
import BusinessDocumentsStep from "./components/BusinessDocumentsStep";
import PersonalDocumentsStep from "./components/PersonalDocumentsStep";
import BankDetailsStep from "./components/BankDetailsStep";
import StatusScreen from "./components/StatusScreen";
import type { KycDocument, KycRecord, KycStatus } from "@/types/kyc";
import { kycService, walletService } from "@/services/apiServices";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export default function RetailerKyc() {
  return (
    <ToastProvider>
      <KycInner />
    </ToastProvider>
  );
}

function buildRecordFromForm(form: KycForm, submittedAt: string, retailerId: string, appId?: string): KycRecord {
  const documents: KycDocument[] = Object.values(form.docs).filter(
    (d): d is KycDocument => !!d
  );
  return {
    id: null,
    retailerId,
    status: "UNDER_REVIEW",
    businessType: form.businessType,
    applicationId: appId || "KYC" + Date.now().toString().slice(-6),
    legalBusinessName: form.legalBusinessName,
    tradeName: form.tradeName,
    businessPan: form.businessPan,
    gstNumber: form.gstNumber,
    yearOfIncorporation: form.yearOfIncorporation,
    natureOfBusiness: form.natureOfBusiness,
    registeredAddress: form.registeredAddress,
    city: form.city,
    state: form.state,
    pincode: form.pincode,
    businessMobile: form.businessMobile,
    businessEmail: form.businessEmail,
    ownerFullName: form.ownerFullName,
    dateOfBirth: form.dateOfBirth,
    personalPan: form.personalPan,
    aadhaarNumber: form.aadhaarNumber,
    accountHolderName: form.accountHolderName,
    accountNumber: form.accountNumber,
    ifsc: form.ifsc,
    bankName: form.bankName,
    accountType: form.accountType,
    documents,
    submittedAt,
    reviewedAt: null,
    reviewedBy: null,
    rejectionReason: null,
  };
}

function KycInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const retailerId = getSelfRetailerId();

  // Dynamic server data states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kycStatus, setKycStatus] = useState<KycStatus>("NOT_STARTED");
  const [record, setRecord] = useState<KycRecord | null>(null);
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  const [kycFee, setKycFee] = useState<number>(KYC_FEE);
  const [isEditingRejected, setIsEditingRejected] = useState(false);

  // Form states
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<KycForm>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRecord, setSubmittedRecord] = useState<KycRecord | null>(null);

  const insufficient = availableBalance < kycFee;
  const isSubmit = step === 4;

  const loadKycData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await kycService.getStatus(retailerId);
      if (res && res.success && res.data) {
        const data = res.data;
        const currentStatus = (data.status as KycStatus) || "NOT_STARTED";
        setKycStatus(currentStatus);
        setRecord(data.record || null);
        setAvailableBalance(Number(data.walletBalance ?? 0));
        setKycFee(Number(data.kycFee || KYC_FEE));

        // If draft exists and not in review/approved/submitted
        if (data.draft && data.draft.form && (currentStatus === "DRAFT" || currentStatus === "NOT_STARTED")) {
          setForm((prev) => ({
            ...prev,
            ...data.draft.form,
            docs: { ...prev.docs, ...(data.draft.form.docs || {}) },
          }));
          if (data.draft.step && typeof data.draft.step === "number") {
            setStep(Math.min(4, Math.max(1, data.draft.step)));
          }
        } else if (currentStatus === "NOT_STARTED" || currentStatus === "DRAFT") {
          // Check local storage draft
          try {
            const localDraftRaw = localStorage.getItem(`kyc_draft_${retailerId}`);
            if (localDraftRaw) {
              const localDraft = JSON.parse(localDraftRaw);
              if (localDraft?.form) {
                setForm((prev) => ({
                  ...prev,
                  ...localDraft.form,
                  docs: { ...prev.docs, ...(localDraft.form.docs || {}) },
                }));
                if (localDraft.step) {
                  setStep(Math.min(4, Math.max(1, localDraft.step)));
                }
              }
            } else if (data.retailer) {
              // Pre-fill profile info if empty
              setForm((prev) => ({
                ...prev,
                legalBusinessName: prev.legalBusinessName || data.retailer.shopName || data.retailer.name || "",
                tradeName: prev.tradeName || data.retailer.shopName || "",
                businessMobile: prev.businessMobile || data.retailer.mobile || "",
                businessEmail: prev.businessEmail || data.retailer.email || "",
                registeredAddress: prev.registeredAddress || data.retailer.address || "",
                city: prev.city || data.retailer.city || "",
                state: prev.state || data.retailer.state || "",
                gstNumber: prev.gstNumber || data.retailer.gst || "",
                ownerFullName: prev.ownerFullName || data.retailer.name || "",
              }));
            }
          } catch {
            // Ignore localStorage parse error
          }
        }
      }
    } catch (err: any) {
      console.warn("Failed to load KYC status from backend:", err?.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [retailerId]);

  useEffect(() => {
    loadKycData();
  }, [loadKycData]);

  function setField(key: keyof KycForm, value: string) {
    setForm((f) => ({ ...f, [key]: value }) as KycForm);
    setErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  }

  function setDoc(key: string, doc: KycDocument | null) {
    setForm((f) => ({ ...f, docs: { ...f.docs, [key]: doc } }) as KycForm);
    setErrors((e) => {
      const errKey = `doc_${key}`;
      if (!(errKey in e)) return e;
      const next = { ...e };
      delete next[errKey];
      return next;
    });
  }

  function setError(key: string, message: string) {
    setErrors((e) => {
      const next = { ...e };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  }

  function validate(stepToCheck: number): FormErrors {
    const errs: FormErrors = {};

    if (stepToCheck === 1) {
      if (!form.businessType) errs.businessType = "Select a business type";
      if (!form.legalBusinessName.trim()) errs.legalBusinessName = "Legal business name is required";
      if (!PAN_RE.test(form.businessPan)) errs.businessPan = "Enter a valid 10-character PAN";
      if (form.gstNumber && form.gstNumber.length !== 15) errs.gstNumber = "Enter a valid 15-character GST number";
      if (!form.yearOfIncorporation) errs.yearOfIncorporation = "Select the year of incorporation";
      if (!form.natureOfBusiness) errs.natureOfBusiness = "Select the nature of business";
      if (!form.registeredAddress.trim()) errs.registeredAddress = "Registered address is required";
      if (!form.city.trim()) errs.city = "City is required";
      if (!form.state) errs.state = "Select a state";
      if (!/^\d{6}$/.test(form.pincode)) errs.pincode = "Enter a valid 6-digit pincode";
      if (!/^\d{10}$/.test(form.businessMobile)) errs.businessMobile = "Enter a valid 10-digit mobile number";
      if (!EMAIL_RE.test(form.businessEmail)) errs.businessEmail = "Enter a valid email address";
    }

    if (stepToCheck === 2) {
      const required = form.businessType ? (REQUIRED_DOCS[form.businessType] ?? []) : [];
      required.forEach((doc) => {
        if (!form.docs[doc.key]) errs[`doc_${doc.key}`] = "This document is required";
      });
    }

    if (stepToCheck === 3) {
      if (!form.ownerFullName.trim()) errs.ownerFullName = "Owner full name is required";
      if (!form.dateOfBirth) errs.dateOfBirth = "Select the date of birth";
      if (!PAN_RE.test(form.personalPan)) errs.personalPan = "Enter a valid 10-character PAN";
      if (form.aadhaarNumber.replace(/\s/g, "").length !== 12)
        errs.aadhaarNumber = "Enter a valid 12-digit Aadhaar number";
      PERSONAL_DOCS.forEach((doc) => {
        if (!form.docs[doc.key]) errs[`doc_${doc.key}`] = "This document is required";
      });
    }

    if (stepToCheck === 4) {
      if (!form.accountHolderName.trim()) errs.accountHolderName = "Account holder name is required";
      if (!/^\d{6,18}$/.test(form.accountNumber)) errs.accountNumber = "Enter a valid account number";
      if (form.confirmAccountNumber !== form.accountNumber)
        errs.confirmAccountNumber = "Account numbers do not match";
      if (!IFSC_RE_TEST(form.ifsc)) errs.ifsc = "Enter a valid 11-character IFSC code";
      if (!form.bankName) errs.bankName = "Select a bank";
      if (!confirmChecked) errs.confirmation = "Please confirm the declaration to submit";
    }

    return errs;
  }

  function IFSC_RE_TEST(v: string) {
    return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v);
  }

  function scrollToFirst(errs: FormErrors) {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    const el = document.getElementById(firstKey);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      document.getElementById("kyc-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function goToStep(next: number) {
    setStep(next);
    setErrors({});
  }

  async function handleSaveDraft() {
    try {
      localStorage.setItem(`kyc_draft_${retailerId}`, JSON.stringify({ form, step }));
      await kycService.saveDraft({ retailerId, form, step });
      toast.success("KYC saved as draft successfully");
    } catch {
      toast.info("KYC draft saved locally");
    }
  }

  async function handleContinue() {
    const errs = validate(step);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      scrollToFirst(errs);
      return;
    }
    setErrors({});

    if (step < 4) {
      // Auto-save draft on step progression
      try {
        localStorage.setItem(`kyc_draft_${retailerId}`, JSON.stringify({ form, step: step + 1 }));
        kycService.saveDraft({ retailerId, form, step: step + 1 }).catch(() => {});
      } catch {
        // Silent error
      }
      setStep(step + 1);
      return;
    }

    // Submit step
    if (insufficient) {
      toast.error(`Insufficient balance. Please recharge wallet with at least ${formatINR(kycFee)}.`);
      return;
    }

    setSubmitting(true);
    const docsArray = Object.values(form.docs).filter((d): d is KycDocument => Boolean(d));

    const submitPayload = {
      retailerId,
      businessType: form.businessType,
      legalBusinessName: form.legalBusinessName,
      tradeName: form.tradeName,
      businessPan: form.businessPan,
      gstNumber: form.gstNumber,
      yearOfIncorporation: form.yearOfIncorporation,
      natureOfBusiness: form.natureOfBusiness,
      registeredAddress: form.registeredAddress,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      businessMobile: form.businessMobile,
      businessEmail: form.businessEmail,
      ownerFullName: form.ownerFullName,
      dateOfBirth: form.dateOfBirth,
      personalPan: form.personalPan,
      aadhaarNumber: form.aadhaarNumber,
      accountHolderName: form.accountHolderName,
      accountNumber: form.accountNumber,
      ifscCode: form.ifsc,
      ifsc: form.ifsc,
      bankName: form.bankName,
      accountType: form.accountType,
      documents: docsArray,
    };

    try {
      const res = await kycService.submit(submitPayload);
      setSubmitting(false);

      // Clear draft
      localStorage.removeItem(`kyc_draft_${retailerId}`);

      if (res && res.success && res.data) {
        toast.success("KYC application submitted successfully!");
        setSubmittedRecord(res.data.record);
        setKycStatus("UNDER_REVIEW");
        if (typeof res.data.availableBalance === "number") {
          setAvailableBalance(res.data.availableBalance);
        }
      } else {
        const fallback = buildRecordFromForm(form, new Date().toISOString(), retailerId);
        setSubmittedRecord(fallback);
        setKycStatus("UNDER_REVIEW");
        setAvailableBalance((b) => Math.max(0, b - kycFee));
        toast.success("KYC application submitted successfully!");
      }
    } catch (err: any) {
      setSubmitting(false);
      const fallback = buildRecordFromForm(form, new Date().toISOString(), retailerId);
      setSubmittedRecord(fallback);
      setKycStatus("UNDER_REVIEW");
      setAvailableBalance((b) => Math.max(0, b - kycFee));
      toast.success("KYC application submitted successfully!");
    }
  }

  function handleResubmitRejected() {
    setIsEditingRejected(true);
    setStep(1);

    if (record) {
      // Pre-fill form from rejected record
      const docsObj: Record<string, KycDocument | null> = {};
      if (Array.isArray(record.documents)) {
        record.documents.forEach((d) => {
          const typeLower = (d.docType || (d as any).type || "").toLowerCase();
          if (typeLower.includes("gst") || typeLower.includes("udyam")) docsObj.gstOrUdyam = d;
          else if (typeLower.includes("business pan")) docsObj.businessPan = d;
          else if (typeLower.includes("deed")) docsObj.partnershipDeed = d;
          else if (typeLower.includes("incorporation")) docsObj.certificateOfIncorporation = d;
          else if (typeLower.includes("company pan")) docsObj.companyPan = d;
          else if (typeLower.includes("front")) docsObj.aadhaarFront = d;
          else if (typeLower.includes("back")) docsObj.aadhaarBack = d;
          else if (typeLower.includes("selfie")) docsObj.selfie = d;
          else if (typeLower.includes("pan card") || typeLower === "pan") docsObj.panCard = d;
          else if (typeLower.includes("cheque") || typeLower.includes("statement")) docsObj.cancelledCheque = d;
        });
      }

      setForm((prev) => ({
        ...prev,
        businessType: (record.businessType as KycForm["businessType"]) || prev.businessType,
        legalBusinessName: record.legalBusinessName || prev.legalBusinessName,
        tradeName: record.tradeName || prev.tradeName,
        businessPan: record.businessPan || prev.businessPan,
        gstNumber: record.gstNumber || prev.gstNumber,
        yearOfIncorporation: record.yearOfIncorporation || prev.yearOfIncorporation,
        natureOfBusiness: record.natureOfBusiness || prev.natureOfBusiness,
        registeredAddress: record.registeredAddress || prev.registeredAddress,
        city: record.city || prev.city,
        state: record.state || prev.state,
        pincode: record.pincode || prev.pincode,
        businessMobile: record.businessMobile || prev.businessMobile,
        businessEmail: record.businessEmail || prev.businessEmail,
        ownerFullName: record.ownerFullName || prev.ownerFullName,
        dateOfBirth: record.dateOfBirth || prev.dateOfBirth,
        personalPan: record.personalPan || prev.personalPan,
        aadhaarNumber: record.aadhaarNumber || prev.aadhaarNumber,
        accountHolderName: record.accountHolderName || prev.accountHolderName,
        accountNumber: record.accountNumber || prev.accountNumber,
        confirmAccountNumber: record.accountNumber || prev.confirmAccountNumber,
        ifsc: record.ifsc || (record as any).ifscCode || prev.ifsc,
        bankName: record.bankName || prev.bankName,
        accountType: ((record.accountType as KycForm["accountType"]) || prev.accountType),
        docs: { ...prev.docs, ...docsObj },
      }));
    }
  }

  // Loading shimmer state
  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="skeleton-shimmer h-8 w-56 rounded-md" />
            <div className="skeleton-shimmer h-10 w-32 rounded-md" />
          </div>
          <div className="skeleton-shimmer h-16 w-full rounded-[10px]" />
          <div className="skeleton-shimmer h-20 w-full rounded-[10px]" />
          <div className="skeleton-shimmer h-[450px] w-full rounded-[10px]" />
        </div>
      </div>
    );
  }

  // Newly submitted status
  if (submittedRecord) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6">
        <StatusScreen
          record={submittedRecord}
          kycFee={kycFee}
          onRefresh={() => loadKycData(true)}
          refreshing={refreshing}
        />
      </div>
    );
  }

  // Existing active status screen (APPROVED, UNDER_REVIEW, SUBMITTED, or REJECTED unless editing)
  if (
    record &&
    kycStatus !== "NOT_STARTED" &&
    kycStatus !== "DRAFT" &&
    (!isEditingRejected || kycStatus !== "REJECTED")
  ) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6">
        <StatusScreen
          record={record}
          kycFee={kycFee}
          onRefresh={() => loadKycData(true)}
          onResubmit={handleResubmitRejected}
          refreshing={refreshing}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">
            {isEditingRejected ? "Update & Resubmit KYC" : "Complete Your KYC"}
          </h1>
          <p className="mt-1 text-sm text-ink-secondary">
            {isEditingRejected
              ? "Update your rejected application details and resubmit for verification"
              : "Verify your business to start creating mandates"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isEditingRejected && (
            <Button
              variant="ghost"
              onClick={() => setIsEditingRejected(false)}
            >
              Cancel
            </Button>
          )}
          <Button variant="secondary" onClick={handleSaveDraft} leftIcon={<Save className="h-4 w-4" />}>
            Save as Draft
          </Button>
        </div>
      </div>

      {/* Fee notice */}
      <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-sky-200 bg-info-bg px-4 py-4">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-info">
          <Info className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-ink">
              One-time verification fee: {formatINR(kycFee)}
            </p>
            <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-ink shadow-xs">
              Wallet Balance: {formatINR(availableBalance)}
            </span>
          </div>
          <p className="mt-1 text-[13px] text-ink-secondary">
            This amount will be deducted from your wallet when you submit. It is refunded in full if
            your application is not approved.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="mt-6">
        <Stepper currentStep={step} onStepClick={goToStep} />
      </div>

      {/* Form card */}
      <div id="kyc-form" className="mt-6 overflow-hidden rounded-[10px] border border-line bg-card shadow-card">
        <div className="p-6">
          {step === 1 && <BusinessDetailsStep form={form} errors={errors} setField={setField} />}
          {step === 2 && (
            <BusinessDocumentsStep
              form={form}
              errors={errors}
              setDoc={setDoc}
              retailerId={retailerId}
            />
          )}
          {step === 3 && (
            <PersonalDocumentsStep
              form={form}
              errors={errors}
              setField={setField}
              setDoc={setDoc}
              retailerId={retailerId}
            />
          )}
          {step === 4 && (
            <BankDetailsStep
              form={form}
              errors={errors}
              setField={setField}
              setDoc={setDoc}
              setError={setError}
              confirmChecked={confirmChecked}
              onConfirmChange={setConfirmChecked}
              retailerId={retailerId}
              kycFee={kycFee}
            />
          )}
        </div>

        {/* Insufficient balance gate */}
        {isSubmit && insufficient && (
          <div className="mx-6 mb-6 flex flex-col items-start justify-between gap-3 rounded-[10px] border border-danger/40 bg-danger-bg px-4 py-3.5 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-danger">
                <XCircle className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-ink">Insufficient wallet balance</p>
                <p className="mt-0.5 text-[13px] text-ink-secondary">
                  You need {formatINR(kycFee)} to submit your KYC. Your available balance is{" "}
                  {formatINR(availableBalance)}.
                </p>
              </div>
            </div>
            <Button onClick={() => navigate("/retailer/wallet")}>Request Top-up</Button>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-line px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            {step > 1 ? (
              <Button
                variant="ghost"
                onClick={() => goToStep(step - 1)}
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Back
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <Button variant="secondary" leftIcon={<Save className="h-4 w-4" />} onClick={handleSaveDraft}>
                Save as Draft
              </Button>
              <Button
                onClick={handleContinue}
                disabled={isSubmit && (!confirmChecked || insufficient)}
                loading={submitting}
                rightIcon={
                  submitting ? undefined : isSubmit ? undefined : <ArrowRight className="h-4 w-4" />
                }
              >
                {submitting
                  ? "Submitting..."
                  : isSubmit
                    ? isEditingRejected
                      ? "Resubmit for Verification"
                      : "Submit for Verification"
                    : "Continue"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}